/**
 * Entity-level ACL hooks for the Share dialog and per-entity gated UI.
 *
 * Backed by the PR-3.2 endpoints under `/api/entities/:type/:id/*`.
 * Backend is canonical — these hooks never decide access locally;
 * they reflect the server-resolved state.
 *
 * `useEntityAccess`     — caller's effective permission on an entity.
 * `useEntityAclSnapshot`— full ACL + privacy mode for the dialog.
 * `useCanEdit/Manage` — boolean conveniences for show/hide UI.
 *
 * Mutations live in `useEntityAclMutations` (set mode, grant, revoke,
 * Private-to-me) and invalidate the relevant query keys on success.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isValidUUID } from '@bower/shared/schemas/validation';
import { apiService } from '../../services/api.service';
import { queryKeys } from '../../lib/queryClient';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';

/**
 * Typed error thrown by the entity-ACL mutations.  Carries the
 * backend's structured error code (e.g. `PRIVATE_MODE_DISABLED`,
 * `ENTITY_ACCESS_DENIED`) alongside the human-readable
 * message so callers can branch on the code instead of fragile
 * string matching on `error.message`.
 *
 * Usage:
 *   try { await mut.setMode.mutateAsync(...); }
 *   catch (e) {
 *     if (e instanceof EntityAclError && e.code === 'PRIVATE_MODE_DISABLED') {
 *       // Show tailored toast
 *     }
 *   }
 *
 * Closes review W2 — replaces the previous string-match heuristic
 * which broke if backend copy changed.
 */
export class EntityAclError extends Error {
  readonly code: string | undefined;
  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = 'EntityAclError';
    this.code = code;
  }
}

export type AclEntityType = 'PROJECT' | 'ARTIFACT';
export type EntityPermission = 'VIEW' | 'EDIT' | 'MANAGE';
export type EntityPermissionLevel = 'NONE' | EntityPermission;
export type EntityPrivacyMode = 'WORKSPACE' | 'RESTRICTED' | 'PRIVATE';

export interface EntityAccess {
  permission: EntityPermissionLevel;
  source: 'none' | 'owner' | 'workspace' | 'explicit' | 'inherited';
}

export interface EntityAclSnapshotEntry {
  id: string;
  principalUserId: string;
  permission: EntityPermission;
  grantedAt: string;
  user: { id: string; name: string | null; avatar: string | null };
  /**
   * Whether this person can actually open the entity (the server's access
   * guard). A grant cannot pierce a restricted or private ancestor, so a
   * person can be listed here and still be unable to open it.
   */
  canOpen: boolean;
  /** When `canOpen` is false, the collection that explains it, if one does. */
  blockedBy: (AclInheritanceSource & { mode: 'RESTRICTED' | 'PRIVATE' }) | null;
}

/**
 * The ancestor that the snapshot's effective ACL is inherited from.
 * Surfaced in the Share dialog as "Access list inherited from X" so
 * admins know where to navigate to change grants.  Null when the
 * entity has its own ACL or is in WORKSPACE mode.
 */
export interface AclInheritanceSource {
  entityType: AclEntityType;
  entityId: string;
  name: string;
}

export interface EntityAclSnapshot {
  entityType: AclEntityType;
  entityId: string;
  mode: EntityPrivacyMode;
  inherited: boolean;
  acl: ReadonlyArray<EntityAclSnapshotEntry>;
  aclInheritedFrom: AclInheritanceSource | null;
  /**
   * Ancestor that this entity's PRIVACY MODE is inherited from.
   * Set whenever `inherited === true` AND there is at least one
   * ancestor with an explicit `EntityPrivacy` row.  `null` when the
   * entity has its own privacy row, or when no ancestor has one
   * (workspace default applies).
   */
  privacyInheritedFrom: AclInheritanceSource | null;
  /**
   * This entity's OWN privacy setting, or null when it has none.
   *
   * A descendant's own setting can only NARROW an ancestor's, never
   * broaden it — so a WORKSPACE row inside a PRIVATE collection is a
   * DORMANT preference that takes effect only if the entity moves out.
   */
  ownMode: EntityPrivacyMode | null;
  /** The mode that actually governs reads. Never broader than ownMode. */
  effectiveMode: EntityPrivacyMode;
  /** Every boundary the caller must satisfy, nearest first. */
  effectiveBoundaries: ReadonlyArray<AclInheritanceSource & { mode: 'RESTRICTED' | 'PRIVATE' }>;
  /**
   * The user id of whoever created this entity.  The Share dialog
   * renders them with a "Creator" badge and locks their permission
   * row to MANAGE — Notion convention.
   */
  createdById: string | null;
}

const ORDER: Record<EntityPermissionLevel, number> = {
  NONE: 0,
  VIEW: 1,
  EDIT: 2,
  MANAGE: 3,
};

/**
 * Per-workspace ACL enforcement flags.  Backend `acl-flags.service`
 * reads these from `Workspace.settings` JSONB.  Frontend gates UI
 * surfaces (Share dialog, "Manage access" menu item, sidebar lock
 * badges, "Shared with me" section) on `aclEnforceReads === true`
 * because surfacing those controls when ops hasn't enabled
 * enforcement would let the user flip privacy modes that have no
 * effect on what reads return — confusing, possibly dangerous if
 * users assume they're protected.
 */
export interface AclFeatureFlags {
  aclEnforceReads: boolean;
  aclEnforceWrites: boolean;
  aclEnforceRls: boolean;
}

export function useAclFeatureFlags() {
  const workspaceId = useWorkspaceId();

  return useQuery({
    queryKey: workspaceId
      ? queryKeys.entityAcl.featureFlags(workspaceId)
      : ['entityAcl', 'featureFlags', 'disabled'],
    queryFn: async () => {
      const res = await apiService.get<AclFeatureFlags>('/workspace/feature-flags/acl');
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to load ACL flags');
      return res.data as AclFeatureFlags;
    },
    enabled: !!workspaceId,
    // Flag changes are rare ops actions; cache for 5 minutes.
    // Refetch on focus so a flag flipped server-side is picked up
    // within ~one tab-switch of the user.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Convenience: is the read-side ACL enforcement on for this workspace?
 * Treats loading state as "not enforced" — better to hide UI controls
 * during the brief load than to show controls that may flicker off
 * when the flags resolve.  Returns `false` until the query confirms
 * `true`, so first-paint never shows ungated UI.
 */
export function useIsAclEnforced(): boolean {
  const { data } = useAclFeatureFlags();
  return data?.aclEnforceReads === true;
}

/**
 * Should we issue an `/entities/:type/:id/*` request for this id?
 *
 * `Artifact.id` and `Project.id` are both `@default(uuid())`, so a
 * path segment that isn't UUID-shaped cannot resolve to a row — the
 * request is a guaranteed 404.  Truthiness alone isn't enough of a
 * gate: a route param happily carries a hand-typed or agent-authored
 * placeholder (`/notes/new_id`, `/attachments/artifact_id_not_found`
 * were both seen in production), and each such page load fired doomed
 * requests plus the `API Error Occurred` events they emit.
 *
 * Deliberately a positive shape check rather than a denylist of known
 * placeholders — the observed strings appear nowhere in this codebase,
 * so there is no fixed set to enumerate.  It uses the shared (lenient)
 * `isValidUUID`, which accepts any RFC-4122 version, so it cannot
 * false-negative a legitimate id.  That matters: callers turn "no
 * access data" into a read-only editor (`EntityWriteContext`,
 * `useNoteDetail`), so wrongly skipping a real entity's check would be
 * worse than the noise this prevents.
 *
 * SCOPE — the two queries in this file only.  Do NOT also apply it to
 * `useRestrictedEntityInfo`: `ProjectDetail`, `useNoteDetail` and
 * `AttachmentDetail` each compute `splashPending = detailFailed &&
 * !isRestrictedInfoFetched`, and a query that never runs holds
 * `isFetched` false forever — the not-found page would become a
 * skeleton that never resolves, with the error suppressed.  That needs
 * the three call sites fixed first; `useEntityAccess.idGuard.test.ts`
 * pins the current behaviour.  The two hooks here are safe because no
 * consumer reads their `isFetched`.
 */
export function isFetchableEntityId(entityId: string | undefined): boolean {
  return entityId !== undefined && isValidUUID(entityId);
}

/**
 * Caller's effective permission on the entity.  Returns `'NONE'` when
 * the user lacks access — the backend deliberately doesn't 403 this
 * endpoint so the UI can render contextual "you don't have access"
 * messaging without an extra round-trip.
 */
export function useEntityAccess(
  entityType: AclEntityType | undefined,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey:
      entityType && entityId
        ? queryKeys.entityAcl.access(entityType, entityId)
        : ['entityAcl', 'access', 'disabled'],
    queryFn: async () => {
      const res = await apiService.get<EntityAccess>(`/entities/${entityType}/${entityId}/access`);
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to load access');
      return res.data as EntityAccess;
    },
    enabled: !!entityType && isFetchableEntityId(entityId),
    staleTime: 30 * 1000,
  });
}

/**
 * Full ACL + privacy snapshot for the Share dialog.
 *
 * Backend 403s if the caller can't even read the entity — TanStack
 * Query throws and the dialog can render a "you don't have access"
 * state.  In normal flow the dialog is only opened by callers with
 * MANAGE, so this is a defense-in-depth path.
 */
export function useEntityAclSnapshot(
  entityType: AclEntityType | undefined,
  entityId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey:
      entityType && entityId
        ? queryKeys.entityAcl.snapshot(entityType, entityId)
        : ['entityAcl', 'snapshot', 'disabled'],
    queryFn: async () => {
      const res = await apiService.get<EntityAclSnapshot>(
        `/entities/${entityType}/${entityId}/acl`,
      );
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to load ACL');
      return res.data as EntityAclSnapshot;
    },
    enabled: enabled && !!entityType && isFetchableEntityId(entityId),
    staleTime: 30 * 1000,
  });
}

// ─── Boolean conveniences ────────────────────────────────────────────

function meetsPermission(access: EntityAccess | undefined, required: EntityPermission): boolean {
  if (!access) return false;
  return ORDER[access.permission] >= ORDER[required];
}

export function useCanEdit(entityType: AclEntityType, entityId: string | undefined): boolean {
  const { data } = useEntityAccess(entityType, entityId);
  return meetsPermission(data, 'EDIT');
}

export function useCanManage(entityType: AclEntityType, entityId: string | undefined): boolean {
  const { data } = useEntityAccess(entityType, entityId);
  return meetsPermission(data, 'MANAGE');
}

// ─── Mutations ───────────────────────────────────────────────────────

export interface EntityAclMutationContext {
  entityType: AclEntityType;
  entityId: string;
}

/**
 * Mutations bundled together so the dialog can grab them once and
 * invalidate the same query keys on success.  Caller passes the
 * (entityType, entityId) once at the dialog level.
 */
export function useEntityAclMutations({ entityType, entityId }: EntityAclMutationContext) {
  const qc = useQueryClient();

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: queryKeys.entityAcl.access(entityType, entityId) });
    void qc.invalidateQueries({ queryKey: queryKeys.entityAcl.snapshot(entityType, entityId) });
    // ACL changes can move entities into / out of the user's "Shared
    // with me" set (e.g. granting access to an entity in a restricted
    // subtree).  Invalidate the whole hierarchy namespace so both the
    // regular tree and the orphan list refetch.
    void qc.invalidateQueries({ queryKey: ['hierarchy'] });
  };

  const setMode = useMutation({
    mutationFn: async (mode: EntityPrivacyMode) => {
      const res = await apiService.put<{ mode: EntityPrivacyMode }>(
        `/entities/${entityType}/${entityId}/privacy`,
        { mode },
      );
      if (!res.success)
        throw new EntityAclError(res.error?.message ?? 'Failed to set privacy', res.error?.code);
      return res.data as { mode: EntityPrivacyMode };
    },
    onSuccess: invalidate,
  });

  const grant = useMutation({
    mutationFn: async (input: { userId: string; permission: EntityPermission }) => {
      // The grant response omits canOpen/blockedBy: only the snapshot read
      // computes them, so never write this into the snapshot cache.
      type GrantResult = Omit<EntityAclSnapshotEntry, 'canOpen' | 'blockedBy'>;
      const res = await apiService.put<GrantResult>(
        `/entities/${entityType}/${entityId}/acl`,
        input,
      );
      if (!res.success)
        throw new EntityAclError(res.error?.message ?? 'Failed to grant access', res.error?.code);
      return res.data as GrantResult;
    },
    onSuccess: invalidate,
  });

  const revoke = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiService.delete<{ revoked: true }>(
        `/entities/${entityType}/${entityId}/acl/${userId}`,
      );
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to revoke access');
      return res.data;
    },
    onSuccess: invalidate,
  });

  const makePrivate = useMutation({
    mutationFn: async () => {
      const res = await apiService.post<{ mode: EntityPrivacyMode }>(
        `/entities/${entityType}/${entityId}/private`,
        {},
      );
      if (!res.success)
        throw new EntityAclError(res.error?.message ?? 'Failed to make private', res.error?.code);
      return res.data as { mode: EntityPrivacyMode };
    },
    onSuccess: invalidate,
  });

  /**
   * Drop the entity's explicit privacy row → revert to inheriting
   * from the nearest ancestor with one (or workspace default).
   * Distinct from `setMode('WORKSPACE')` which forces WORKSPACE
   * regardless of ancestor; this lets future ancestor changes flow
   * down automatically.  Invoked by the dialog's "Use parent's
   * settings" action when the entity has its own privacy row.
   */
  const resetToInherited = useMutation({
    mutationFn: async () => {
      const res = await apiService.delete<{ inherited: true }>(
        `/entities/${entityType}/${entityId}/privacy`,
      );
      if (!res.success) throw new Error(res.error?.message ?? 'Failed to reset privacy');
      return res.data;
    },
    onSuccess: invalidate,
  });

  return { setMode, grant, revoke, makePrivate, resetToInherited, invalidate };
}
